import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import clsx from "clsx";
import { ComponentProps } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "_shared/contexts/AuthContext";
import { useGuidedTour } from "_shared/contexts/GuidedTourContext";
import { useOpenTicket } from "_shared/contexts/OpenTicketContext";
import styles from "./NavMenu.module.scss";

const Link = (props: ComponentProps<RouterLink>) => {
  const location = useLocation();
  const isActive =
    (props.to === "/login" && location.pathname === "/") ||
    location.pathname.startsWith(props.to as string);

  return (
    <NavigationMenu.Link asChild active={isActive} className={styles.link}>
      <RouterLink {...props} />
    </NavigationMenu.Link>
  );
};

interface Props {
  isAuthenticated: boolean;
}

function NavMenu({ isAuthenticated }: Props) {
  const { signOut } = useAuth();
  const { openGuidedTour } = useGuidedTour();
  const { toggleOpenTicket } = useOpenTicket();
  return (
    <NavigationMenu.Root>
      {!isAuthenticated ? (
        <NavigationMenu.List className={styles.list}>
          <NavigationMenu.Item data-testid="navlink">
            <Link to={"/login"}>Log in</Link>
          </NavigationMenu.Item>
          <NavigationMenu.Item
            data-testid="navlink"
            className={styles.highlight}
          >
            <Link to="/signup" data-testid="navlink-signup">
              Sign up
            </Link>
          </NavigationMenu.Item>
        </NavigationMenu.List>
      ) : (
        <NavigationMenu.List className={styles.list}>
          <NavigationMenu.Item data-testid="navlink">
            <Link to={"/dashboard"}>Dashboard</Link>
          </NavigationMenu.Item>
          <NavigationMenu.Item data-testid="navlink">
            <NavigationMenu.Trigger asChild>
              <Link
                to={"/account"}
                data-testid="navlink-account"
                className="guidedTour8"
              >
                Account
                <i className={clsx(`fas fa-caret-down`, styles.arrow)} />
              </Link>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className={styles.dropdown}>
              <NavigationMenu.Sub>
                <NavigationMenu.List>
                  <NavigationMenu.Item>
                    <Link to="/account/profile">Profile</Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <Link to="/account/manage-subscriptions">
                      Manage subscriptions
                    </Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <Link to="/account/payment">Payment</Link>
                  </NavigationMenu.Item>
                  {/* <NavigationMenu.Item>
                    <Link to="/account/integrations">Integrations</Link>
                  </NavigationMenu.Item> */}
                  <NavigationMenu.Item>
                    <NavigationMenu.Link asChild>
                      <button
                        className={styles.link}
                        onClick={signOut}
                        data-testid="dropdown-signout"
                      >
                        Sign out
                      </button>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </NavigationMenu.Sub>
            </NavigationMenu.Content>
          </NavigationMenu.Item>
          <NavigationMenu.Item data-testid="navlink">
            <NavigationMenu.Trigger
              className={styles.link}
              data-testid="navlink-help"
            >
              Help
              <i className={clsx(`fas fa-caret-down`, styles.arrow)} />
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className={styles.dropdown}>
              <NavigationMenu.Sub>
                <NavigationMenu.List>
                  <NavigationMenu.Item>
                    <button
                      className={styles.link}
                      onClick={openGuidedTour}
                      data-testid="helplink-tour"
                    >
                      Guided Tour
                    </button>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item data-testid="helplink-support">
                    <button className={styles.link} onClick={toggleOpenTicket}>
                      Open a ticket
                    </button>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <Link
                      to={{ pathname: "https://mylongbow.com/faq" }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      FAQ
                    </Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <Link
                      to={{ pathname: "https://mylongbow.com/faq/#archer" }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Definitions
                    </Link>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </NavigationMenu.Sub>
            </NavigationMenu.Content>
          </NavigationMenu.Item>
        </NavigationMenu.List>
      )}
    </NavigationMenu.Root>
  );
}

export default NavMenu;
